import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowsClockwise, CheckCircle, XCircle, WhatsappLogo, Copy, PaperPlaneTilt,
  Warning, ArrowDown, ArrowUp, FloppyDisk,
} from "@phosphor-icons/react";

const StatusPill = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-sm ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
    {ok ? <CheckCircle size={13} weight="fill" /> : <XCircle size={13} weight="fill" />}
    {label}
  </span>
);

const LANGS = [
  { code: "en_US", label: "English (US)" },
  { code: "en_GB", label: "English (UK)" },
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "hi_IN", label: "Hindi (India)" },
];

const TEMPLATE_KEYS = [
  { key: "welcome", label: "Welcome", hint: "Sent right after a new signup" },
  { key: "upload_reminder", label: "Upload Reminder", hint: "If no Excel imported within 24h" },
  { key: "demo_confirmation", label: "Demo Confirmation", hint: "When a demo is requested" },
  { key: "demo_reminder", label: "Demo Reminder", hint: "24h after a demo request (no conversion)" },
];

const STATUS_COLORS = {
  sent: "bg-blue-100 text-blue-700",
  delivered: "bg-emerald-100 text-emerald-700",
  read: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  skipped: "bg-amber-100 text-amber-700",
  pending: "bg-slate-100 text-slate-600",
  received: "bg-violet-100 text-violet-700",
};

const Field = ({ label, value, mono = true, testId }) => (
  <div>
    <div className="label-caps text-muted-foreground">{label}</div>
    <div className={`mt-0.5 text-sm ${mono ? "mono" : "font-semibold"}`} data-testid={testId}>{value || "—"}</div>
  </div>
);

export default function WhatsAppIntegration() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [test, setTest] = useState({ phone: "", template_name: "hello_world", parameters: "", language_code: "en_US" });
  const [sending, setSending] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const webhookUrl = `${API}/webhooks/whatsapp`;

  const loadStatus = () => {
    setLoading(true);
    api.get("/admin/whatsapp/status").then((r) => setStatus(r.data)).catch(() => setStatus(null)).finally(() => setLoading(false));
  };
  const loadMessages = () => {
    api.get("/admin/whatsapp/messages?limit=100").then((r) => setMessages(r.data || [])).catch(() => setMessages([]));
  };
  const loadConfig = () => {
    api.get("/admin/whatsapp/templates").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  };
  useEffect(() => { loadStatus(); loadMessages(); loadConfig(); }, []);

  const setTpl = (key, field, value) => {
    setCfg((c) => ({ ...c, templates: { ...c.templates, [key]: { ...c.templates[key], [field]: value } } }));
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const r = await api.put("/admin/whatsapp/templates", { default_language: cfg.default_language, templates: cfg.templates });
      setCfg(r.data);
      toast.success("Template configuration saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSavingCfg(false); }
  };

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!test.phone.trim()) return toast.error("Enter a recipient phone number");
    setSending(true);
    try {
      const params = test.parameters.trim() ? test.parameters.split("|").map((p) => p.trim()).filter(Boolean) : null;
      const res = await api.post("/admin/whatsapp/test", {
        phone: test.phone.trim(),
        template_name: test.template_name.trim() || "hello_world",
        parameters: params,
        language_code: test.language_code.trim() || undefined,
      });
      toast.success(`Test message sent (${res.data.message_id || "queued"})`);
      loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const connected = !!status?.connected;

  return (
    <div data-testid="whatsapp-integration-page">
      <PageHeader title="WhatsApp Integration" subtitle="Meta WhatsApp Cloud API — connection, test messaging and delivery logs." />

      <div className="flex items-center gap-3 mb-4">
        <Button variant="outline" className="rounded-sm gap-1.5" onClick={() => { loadStatus(); loadMessages(); }} data-testid="whatsapp-refresh-btn">
          <ArrowsClockwise size={15} /> Refresh
        </Button>
        {status && !status.configured && (
          <span className="inline-flex items-center gap-1.5 text-sm text-amber-700 font-semibold" data-testid="whatsapp-not-configured-warning">
            <Warning size={16} weight="fill" /> Not configured — set WHATSAPP_ACCESS_TOKEN, WHATSAPP_PHONE_NUMBER_ID & WHATSAPP_BUSINESS_ACCOUNT_ID in the backend environment.
          </span>
        )}
      </div>

      {/* Connection status */}
      <div className="border border-border bg-card p-5 mb-6" data-testid="whatsapp-status-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
            <span className="font-bold font-heading">Connection Status</span>
          </div>
          {loading ? <span className="text-xs text-muted-foreground">Checking…</span> : <StatusPill ok={connected} label={connected ? "Connected" : "Disconnected"} />}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Phone Number" value={status?.display_phone_number} testId="whatsapp-phone-number" />
          <Field label="Verified Name" value={status?.verified_name} mono={false} testId="whatsapp-verified-name" />
          <Field label="Phone Number ID" value={status?.phone_number_id} testId="whatsapp-phone-number-id" />
          <Field label="WABA ID" value={status?.business_account_id} testId="whatsapp-waba-id" />
          <Field label="Business Verification" value={status?.business_verification} mono={false} testId="whatsapp-business-verification" />
          <Field label="Quality Rating" value={status?.quality_rating} mono={false} testId="whatsapp-quality-rating" />
          <Field label="Number Verification" value={status?.code_verification_status} mono={false} testId="whatsapp-code-verification" />
          <Field label="API Version" value={status?.api_version} testId="whatsapp-api-version" />
        </div>
        {status?.error && status.error !== "not_configured" && (
          <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="whatsapp-status-error">
            {status.error}
          </div>
        )}
      </div>

      {/* Webhook */}
      <div className="border border-border bg-secondary/40 p-4 mb-6" data-testid="whatsapp-webhook-card">
        <div className="label-caps text-muted-foreground mb-1">Webhook Callback URL</div>
        <div className="flex items-center gap-2">
          <code className="text-sm mono break-all flex-1">{webhookUrl}</code>
          <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => copy(webhookUrl, "Webhook URL")} data-testid="whatsapp-copy-webhook">
            <Copy size={14} /> Copy
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Register this in Meta → WhatsApp → Configuration. Verify token status:{" "}
          <span className={status?.verify_token_set ? "text-green-700 font-semibold" : "text-red-700 font-semibold"}>
            {status?.verify_token_set ? "Set ✓" : "Not set"}
          </span>{" "}
          (the token value is kept server-side and never shown here). Subscribe to the <b>messages</b> field.
        </p>
      </div>

      {/* Template configuration */}
      <div className="border border-border bg-card p-5 mb-6" data-testid="whatsapp-templates-card">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <WhatsappLogo size={18} weight="fill" className="text-[#25D366]" />
            <span className="font-bold font-heading">Message Templates</span>
          </div>
          <Button size="sm" className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#092f6f] text-white" onClick={saveConfig} disabled={savingCfg || !cfg} data-testid="whatsapp-templates-save">
            <FloppyDisk size={15} weight="fill" /> {savingCfg ? "Saving…" : "Save Templates"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Configure the approved Meta template name + language for each automation. Change these anytime after Meta approval — no redeploy needed.
        </p>

        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
          <Label className="text-xs whitespace-nowrap">Default Language</Label>
          <div className="w-56">
            <Select value={cfg?.default_language || "en_US"} onValueChange={(v) => setCfg((c) => ({ ...c, default_language: v }))}>
              <SelectTrigger className="rounded-sm h-9" data-testid="whatsapp-default-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label} · {l.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3">
          {TEMPLATE_KEYS.map(({ key, label, hint }) => {
            const t = cfg?.templates?.[key] || { template_name: "", language: "", enabled: true };
            return (
              <div key={key} className="grid gap-3 md:grid-cols-[1.3fr_1.2fr_1fr_auto] items-end border border-border/60 rounded-sm p-3" data-testid={`whatsapp-template-row-${key}`}>
                <div>
                  <Label className="text-xs font-semibold">{label}</Label>
                  <div className="text-[11px] text-muted-foreground mb-1">{hint}</div>
                  <Input value={t.template_name} onChange={(e) => setTpl(key, "template_name", e.target.value)} placeholder="approved_template_name" className="rounded-sm mono text-sm" data-testid={`whatsapp-template-name-${key}`} />
                </div>
                <div>
                  <Label className="text-xs">Language</Label>
                  <Select value={t.language || cfg?.default_language || "en_US"} onValueChange={(v) => setTpl(key, "language", v)}>
                    <SelectTrigger className="rounded-sm h-9 mt-1" data-testid={`whatsapp-template-lang-${key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label} · {l.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div />
                <div className="flex items-center gap-2 pb-2">
                  <Switch checked={!!t.enabled} onCheckedChange={(v) => setTpl(key, "enabled", v)} data-testid={`whatsapp-template-enabled-${key}`} />
                  <span className="text-xs text-muted-foreground">{t.enabled ? "Enabled" : "Off"}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Send test */}
      <div className="border border-border bg-card p-5 mb-6" data-testid="whatsapp-test-card">
        <div className="flex items-center gap-2 mb-3">
          <PaperPlaneTilt size={18} weight="fill" className="text-[#0B3D91]" />
          <span className="font-bold font-heading">Send Test Message</span>
        </div>
        <form onSubmit={sendTest} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-end">
          <div className="sm:col-span-2 lg:col-span-4">
            <Label className="text-xs">Select Template <span className="text-muted-foreground font-normal">(prefills name + language)</span></Label>
            <div className="w-full sm:w-72">
              <Select onValueChange={(v) => {
                if (v === "hello_world") { setTest((t) => ({ ...t, template_name: "hello_world", language_code: "en_US" })); return; }
                const tpl = cfg?.templates?.[v];
                if (tpl) setTest((t) => ({ ...t, template_name: tpl.template_name, language_code: tpl.language || cfg?.default_language || "en_US" }));
              }}>
                <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="whatsapp-test-select-template"><SelectValue placeholder="Choose a configured template…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hello_world">hello_world (Meta default test)</SelectItem>
                  {TEMPLATE_KEYS.map(({ key, label }) => {
                    const tn = cfg?.templates?.[key]?.template_name;
                    return tn ? <SelectItem key={key} value={key}>{label} · {tn}</SelectItem> : null;
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Recipient Phone</Label>
            <Input value={test.phone} onChange={(e) => setTest({ ...test, phone: e.target.value })} placeholder="+91 98xxxxxxxx" className="rounded-sm mt-1" data-testid="whatsapp-test-phone" />
          </div>
          <div>
            <Label className="text-xs">Template Name</Label>
            <Input value={test.template_name} onChange={(e) => setTest({ ...test, template_name: e.target.value })} placeholder="hello_world" className="rounded-sm mt-1" data-testid="whatsapp-test-template" />
          </div>
          <div>
            <Label className="text-xs">Language</Label>
            <Input value={test.language_code} onChange={(e) => setTest({ ...test, language_code: e.target.value })} placeholder="en_US" className="rounded-sm mt-1" data-testid="whatsapp-test-lang" />
          </div>
          <div>
            <Label className="text-xs">Body Params <span className="text-muted-foreground font-normal">(| separated)</span></Label>
            <Input value={test.parameters} onChange={(e) => setTest({ ...test, parameters: e.target.value })} placeholder="John | 500" className="rounded-sm mt-1" data-testid="whatsapp-test-params" />
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <Button type="submit" disabled={sending || !connected} className="rounded-sm bg-[#25D366] hover:bg-[#1eb555] text-white gap-1.5" data-testid="whatsapp-test-submit">
              <PaperPlaneTilt size={15} weight="fill" /> {sending ? "Sending…" : "Send Test Message"}
            </Button>
            {!connected && <span className="ml-3 text-xs text-muted-foreground">Connect WhatsApp to enable test sending.</span>}
          </div>
        </form>
      </div>

      {/* Message logs */}
      <div className="border border-border bg-card overflow-x-auto" data-testid="whatsapp-messages-card">
        <div className="p-3 border-b border-border bg-secondary/40 font-semibold text-sm">Recent Message Logs</div>
        <table className="w-full text-sm" data-testid="whatsapp-messages-table">
          <thead>
            <tr className="border-b border-border bg-secondary/20 text-left label-caps text-muted-foreground">
              <th className="p-3">Dir</th>
              <th className="p-3">Time</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Template</th>
              <th className="p-3">Status</th>
              <th className="p-3">Message ID</th>
              <th className="p-3">Detail</th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground" data-testid="whatsapp-messages-empty">No messages yet. Send a test or trigger a signup to see delivery logs here.</td></tr>
            )}
            {messages.map((m, i) => (
              <tr key={m.id || i} className="border-b border-border/60 hover:bg-secondary/20" data-testid={`whatsapp-message-row-${i}`}>
                <td className="p-3">
                  {m.direction === "inbound"
                    ? <ArrowDown size={16} weight="bold" className="text-violet-600" title="Incoming" />
                    : <ArrowUp size={16} weight="bold" className="text-blue-600" title="Outgoing" />}
                </td>
                <td className="p-3 text-muted-foreground whitespace-nowrap">{m.created_at ? new Date(m.created_at).toLocaleString() : "—"}</td>
                <td className="p-3 mono">{m.phone || "—"}</td>
                <td className="p-3 font-semibold">{m.template_name || (m.direction === "inbound" ? "(incoming)" : "—")}</td>
                <td className="p-3">
                  <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-sm ${STATUS_COLORS[m.status] || "bg-slate-100 text-slate-600"}`}>{m.status}</span>
                </td>
                <td className="p-3 mono text-xs text-muted-foreground max-w-[180px] truncate" title={m.message_id || ""}>{m.message_id || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground max-w-[240px] truncate" title={m.failure_reason || m.body || ""}>
                  {m.failure_reason || m.body || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
